import IconDoubleChevronLeft from '@apollo/icons/small/IconDoubleChevronLeft.svg';
import { Button, Divider, Tooltip } from '@apollo/orbit';
import React from 'react';

import { InsightsL3Menu } from 'src/app/graph/operationsPage/insightsL3Nav/InsightsL3Menu';
import { FieldsList } from 'src/app/graph/operationsPage/insightsL3Nav/insightsList/FieldsList';
import {
  OperationsList,
  SelectedOperation,
} from 'src/app/graph/operationsPage/insightsL3Nav/insightsList/OperationsList';
import { SchemaCoordinateList } from 'src/app/graph/operationsPage/insightsL3Nav/insightsList/schemaCoordinateList/SchemaCoordinateList';
import { useInsightsL3NavState } from 'src/app/graph/operationsPage/insightsL3Nav/useInsightsL3NavState';
import { TimeRangeProvider } from 'src/app/graph/operationsPage/timeRangeProvider/TimeRangeProvider';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { FilterProvider } from 'src/components/common/filterProvider/FilterProvider';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useShowSubgraphInsightsUI } from 'src/lib/featureFlags';
import { CoordinateKind } from 'src/lib/graphqlTypes/types';

import { OperationsOverviewButton } from './insightsList/insightsListItem/OperationsOverviewButton';
import { SubgraphsList } from './insightsList/SubgraphsList';

interface Props {
  selectedOperation?: SelectedOperation;
}

export function InsightsL3Nav({ selectedOperation }: Props) {
  const { graphId, graphVariant, insightsTab, to, from } =
    useRouteParams(insightsRouteConfig);
  const { isOpen, onClose } = useInsightsL3NavState();
  const [hasResetOperationsFilter, setHasResetOperationsFilter] =
    React.useState(false);
  const showSubgraphInsights = useShowSubgraphInsightsUI();

  if (!graphId || !isOpen) {
    return null;
  }

  return (
    <div className="flex h-full flex-col pb-6 pl-6 pr-0 pt-8">
      <div className="mb-4 flex justify-between">
        <div>
          <InsightsL3Menu />
        </div>
        <Tooltip label="Collapse">
          <Button
            variant="hidden"
            onClick={onClose}
            leftIcon={<IconDoubleChevronLeft />}
            aria-label="Collapse"
          />
        </Tooltip>
      </div>
      {insightsTab === 'operations' && <OperationsOverviewButton />}
      <Divider />
      <TimeRangeProvider serviceId={graphId}>
        {({ selectedRange }) => {
          return (
            <FilterProvider omitSchemaTag>
              {({ filter, ...props }) => {
                const { allClients, clientFilter } = props;
                const allClientsCount = allClients
                  ? Object.keys(allClients).length
                  : 0;

                const clientFilterCount =
                  clientFilter && clientFilter.length > 0
                    ? clientFilter.length
                    : allClientsCount;

                return (
                  <div className="mt-4 flex min-h-0 flex-1 flex-col">
                    <div className="h-full p-0">
                      {insightsTab === 'operations' ? (
                        <OperationsList
                          selectedOperation={selectedOperation}
                          range={selectedRange}
                          graphId={graphId}
                          graphVariant={graphVariant}
                          bounds={{ from, to }}
                          filter={filter}
                          hasResetOperationsFilter={hasResetOperationsFilter}
                          setHasResetOperationsFilter={
                            setHasResetOperationsFilter
                          }
                          clientFilterCount={clientFilterCount}
                        />
                      ) : insightsTab === 'objectFields' ? (
                        <FieldsList
                          range={selectedRange}
                          bounds={{ from, to }}
                          filter={filter}
                          graphId={graphId}
                          graphVariant={graphVariant}
                          clientFilterCount={clientFilterCount}
                        />
                      ) : insightsTab === 'inputFields' ? (
                        <SchemaCoordinateList
                          range={selectedRange}
                          bounds={{ from, to }}
                          filter={filter}
                          graphId={graphId}
                          graphVariant={graphVariant}
                          coordinateKind={CoordinateKind.INPUT_OBJECT_FIELD}
                          clientFilterCount={clientFilterCount}
                        />
                      ) : insightsTab === 'enumValues' ? (
                        <SchemaCoordinateList
                          range={selectedRange}
                          bounds={{ from, to }}
                          filter={filter}
                          graphId={graphId}
                          graphVariant={graphVariant}
                          coordinateKind={CoordinateKind.ENUM_VALUE}
                          clientFilterCount={clientFilterCount}
                        />
                      ) : insightsTab === 'subgraphs' &&
                        showSubgraphInsights ? (
                        <SubgraphsList
                          range={selectedRange}
                          bounds={{ from, to }}
                          filter={filter}
                          graphId={graphId}
                          graphVariant={graphVariant}
                          clientFilterCount={clientFilterCount}
                        />
                      ) : null}
                    </div>
                  </div>
                );
              }}
            </FilterProvider>
          );
        }}
      </TimeRangeProvider>
    </div>
  );
}
